import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { WHITELIST_ERROR_USER_MULTIPLE_COMPANY } from 'src/app/app.constants';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import {
	PhoneNumber,
	User,
	UserSecurityAnswer,
} from 'src/app/models/data/user';
import { UserProfile } from 'src/app/models/model';
import { ApiService } from 'src/app/services/api.services';
import {
	BannerMessageService,
	BannerMessageType,
} from 'src/app/services/banner-message.service';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { MatButton } from '@angular/material/button';
import { LoadingIndicatorComponent } from '../../../../components/loading-indicator/loading-indicator.component';
import { MatDivider } from '@angular/material/list';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { FormsModule } from '@angular/forms';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { PhoneTypeFormComponent } from '../../../../components/phones-form/phone-type-form.component';
import {
	MatExpansionPanel,
	MatExpansionPanelHeader,
	MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { PhoneNumberPipe } from '../../../../components/pipes';

@Component({
	selector: 'app-user-about',
	templateUrl: './user-about.component.html',
	styleUrls: ['./user-about.component.scss', '../../../common/main.scss'],
	imports: [
		MatButton,
		LoadingIndicatorComponent,
		MatDivider,
		MatSlideToggle,
		FormsModule,
		MatFormField,
		MatLabel,
		MatInput,
		PhoneTypeFormComponent,
		MatExpansionPanel,
		MatExpansionPanelHeader,
		MatExpansionPanelTitle,
		PhoneNumberPipe,
	],
})
export class UserAboutComponent implements OnChanges, OnInit {
	@Input() user!: User;

	editUser: {
		email?: string;
		phoneNumber?: {
			rawPhoneNumber?: string;
			countryCode?: string;
		};
		firstName?: string;
		lastName?: string;
		question?: string;
		answer?: string;
		isWhitelisted?: boolean;
	} = {};

	userId: string | undefined = undefined;
	companyId: string | undefined = undefined;
	activeCompanyId: string | undefined = undefined;

	canEditUserData = false;
	canEditUserWhitelist = false;
	canSeeSecurityQuestion = false;
	securityAnswer: UserSecurityAnswer[] = [];
	displayQuestion = '';
	displayAnswer = '';
	isOracle = false;
	message: string = '';

	// tslint:disable-next-line: variable-name
	private _isEditing = false;
	get isEditing(): boolean {
		return this._isEditing;
	}
	set isEditing(value: boolean) {
		this.populateEditUser();
		this._isEditing = value;
	}

	constructor(
		private apiService: ApiService,
		private activatedRoute: ActivatedRoute,
		private bannerMessageService: BannerMessageService,
		private scService: SystemCapabilityService,
		public readonly loadingService: LoadingIndicatorService
	) {
		// get companyId from navigation state for Companies -> <company> -> Users -> <user>.
		this.userId = this.activatedRoute.snapshot.params.id;
		this.companyId = this.activatedRoute.snapshot.params.companyId;
		this.canSeeSecurityQuestion = this.scService.has(
			SystemCapabilityEnum.globalCompanyManagement
		);
	}

	async ngOnInit(): Promise<void> {
		const userProfile: UserProfile = await this.apiService.getUserProfile();
		this.activeCompanyId = userProfile?.activeRoleDetails?.company?.id;

		// needed because this component is reused in two app routes
		if (this.userId && !this.user) {
			this.user = await this.apiService.getUserDetails(this.userId);
		}

		await this.canEditUser();
		await this.canEditWhitelist();

		if (this.canSeeSecurityQuestion) {
			this.securityAnswer = await this.apiService.getSecurityAnswers(
				this.user.id
			);
			if (this.securityAnswer.length > 0) {
				this.displayQuestion = this.securityAnswer[0]?.questionText;
				this.displayAnswer = this.securityAnswer[0]?.answer;
			} else {
				this.displayQuestion = $localize`:@@userAbout-questionNoValue:No question provided.`;
				this.displayAnswer = $localize`:@@userAbout-answerNoValue:No answer provided.`;
			}
		}
		this.isOracle = this.scService.isOracle;
	}

	async canEditUser(): Promise<void> {
		if (this.companyId === undefined) {
			// showing active company users

			// If user is not verified yet, then URM user can edit.
			if (this.user.verified === false) {
				this.canEditUserData = this.scService.has(
					SystemCapabilityEnum.userRoleManagement
				);
			}
		} else {
			// showing users from company list

			if (this.user.verified === false) {
				// in case something went wrong getting the active company, don't let user edit.
				if (this.activeCompanyId === undefined) {
					console.error('unable to get active company ID.');
					return;
				}

				// If viewing active company's users, then URM user can edit.
				if (this.activeCompanyId === this.companyId) {
					this.canEditUserData = this.scService.has(
						SystemCapabilityEnum.userRoleManagement
					);
				} else {
					// If viewing some other company's users, then GCM user can edit.
					this.canEditUserData = this.scService.has(
						SystemCapabilityEnum.globalCompanyManagement
					);
				}
			}
		}
	}

	async canEditWhitelist(): Promise<void> {
		const companyId = this.companyId ?? this.activeCompanyId;

		if (!companyId) {
			this.canEditUserWhitelist = false;
			return;
		}

		const userSystemCapabilities =
			await this.apiService.getUserCompanySystemCapabilities(this.user.id, [
				{ param: 'companyId', value: companyId },
			]);
		const mobileAccessCapability = '10';
		const userHasMobileAccessCapapility =
			userSystemCapabilities?.find(
				(cap) => cap.systemCapability?.id == mobileAccessCapability
			)?.granted ?? false;
		this.canEditUserWhitelist =
			(this.scService.isCompanyAdmin || this.scService.isOracle) &&
			userHasMobileAccessCapapility;
	}

	populateEditUser(): void {
		this.editUser = {
			email: this.user?.email,
			phoneNumber: this.user?.phoneNumber ?? {
				countryCode: 'us',
				rawPhoneNumber: undefined,
			},
			firstName: this.user?.firstName,
			lastName: this.user?.lastName,
			question: this.securityAnswer[0]?.questionText,
			answer: this.securityAnswer[0]?.answer,
			isWhitelisted: this.user?.isWhitelisted ?? false,
		};
	}

	ngOnChanges(): void {
		this.populateEditUser();
	}

	isValid(): boolean {
		return (
			this.editUser.email !== '' &&
			this.editUser.firstName !== '' &&
			this.editUser.lastName !== ''
		);
	}

	updateUserPhone(phoneNumber: PhoneNumber): void {
		this.editUser.phoneNumber = phoneNumber;
	}

	async saveUser(): Promise<void> {
		this.message = '';
		let messageType: BannerMessageType;
		try {
			const updatedUser = await this.apiService.updateUser(
				this.user.id,
				this.editUser
			);
			// Check if the API sent an error message and display the information to the user
			if (updatedUser.error) {
				switch (updatedUser.error) {
					case WHITELIST_ERROR_USER_MULTIPLE_COMPANY:
						this.message = $localize`:@@userAbout-multipleCompanyWhitelistErrorMessage:Whitelist access is limited to users who belong to a single company.`;
						this.editUser.isWhitelisted = false;
						messageType = BannerMessageType.error;
						break;
					default:
						this.message = updatedUser.error;
						messageType = BannerMessageType.error;
						break;
				}
			} else {
				this.message = $localize`:@@userAbout-saveSuccessMessage:Successfully saved changes to user`;
				messageType = BannerMessageType.success;
				this.isEditing = false;
				this.editUser = updatedUser as unknown as User;
			}
			this.user = updatedUser as unknown as User;
		} catch (err: any) {
			this.message = $localize`:@@userAbout-saveErrorMessage:Error saving changes`;
			console.error('Error saving changes: ', err);
			messageType = BannerMessageType.error;
		}
		this.bannerMessageService.sendMessage(this.message, messageType);
	}

	getApiService() {
		return this.apiService;
	}
}
